import { useMemo } from "react"
import { withNamespaces } from "react-i18next"
import {
    Row,
    Col,
    Table,
    Badge,
    UncontrolledTooltip
} from "reactstrap"

import { FinancialStatmentsTypes } from "../../../../constants"
import { RiInformationLine } from "react-icons/ri"

const LocalFinancialTable = ({ t, data = [] }) => {
    const getYear = (date) =>
        new Date(date).getFullYear()

    const formatValue = (key, value, currency) => {
        if (value === null || value === undefined) return "-"

        if (typeof value !== "number") return value

        const moneyIndicators = [
            "assets",
            "liabilities",
            "capital",
            "income",
            "profit",
            "equity",
            "reserves",
            "depreciation",
            "materials"
        ]

        const percentIndicators = [
            "ratio",
            "margin",
            "percentage"
        ]

        const dayIndicators = [
            "days",
            "turnover"
        ]

        const lowerKey = key.toLowerCase()

        if (moneyIndicators.some(k => lowerKey.includes(k)) && currency) {
            return new Intl.NumberFormat("en-US", {
                style: "currency",
                currency
            }).format(value)
        }

        if (percentIndicators.some(k => lowerKey.includes(k))) {
            return `${value}%`
        }

        if (dayIndicators.some(k => lowerKey.includes(k))) {
            return `${value} days`
        }

        return new Intl.NumberFormat("en-US").format(value)
    }

    const groupedByType = useMemo(() => {
        return data.reduce((acc, item) => {
            if (!acc[item.type]) acc[item.type] = []
            acc[item.type].push(item)
            return acc
        }, {})
    }, [data])

    const renderSection = (title, icon, items, sectionKey) => {
        const years = items
            .map(i => i.year_end_date)
            .sort((a, b) => new Date(b) - new Date(a))

        const allKeys = [
            ...new Set(
                items.flatMap(i =>
                    i[sectionKey] ? Object.keys(i[sectionKey]) : []
                )
            )
        ]

        if (!allKeys.length) return null

        return (
            <Row className="mb-4">
                <Col xs="12">
                    {/* Full Width Section Title */}
                    <div className="mb-2 text-center" style={{ fontWeight: "bold" }}>
                        <i className={`${icon} me-2`} />
                        {title}
                    </div>

                    <Table bordered responsive size="sm" className="mb-0">
                        <thead>
                            <tr>
                                <th
                                    style={{
                                        width: '20%'
                                    }}
                                >{t('Item')}</th>
                                {years.map(date => (
                                    <th key={date} className="text-center">
                                        {getYear(date)}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <th>{t('Currency')}</th>
                                {years.map(date => {
                                    const item = items.find(
                                        i => i.year_end_date === date
                                    )

                                    return (
                                        <td key={date}>
                                            {item?.currency}
                                        </td>
                                    )
                                })}
                            </tr>

                            <tr>
                                <th>{t('Consolidated Accounts')}</th>
                                {years.map(date => {
                                    const item = items.find(
                                        i => i.year_end_date === date
                                    )

                                    return (
                                        <td key={date}>
                                            {item?.consolidated_accounts ? t('Yes') : t('No')}
                                        </td>
                                    )
                                })}
                            </tr>

                            {allKeys.map(key => (
                                <tr key={key}>
                                    <th className="text-capitalize">
                                        {key.replace(/_/g, " ")}
                                    </th>

                                    {years.map(date => {
                                        const item = items.find(
                                            i => i.year_end_date === date
                                        )
                                        const value =
                                            item?.[sectionKey]?.[key]

                                        return (
                                            <td key={date} className="text-end">
                                                {formatValue(
                                                    key,
                                                    value,
                                                    item?.currency
                                                )}
                                            </td>
                                        )
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Col>
            </Row>
        )
    }

    return (
        <div>
            {Object.entries(groupedByType).map(([type, items]) => {

                const sortedItems = items.sort(
                    (a, b) => new Date(b.year_end_date) - new Date(a.year_end_date)
                )

                return (
                    <Row key={type} className="mb-5">
                        <Col xs="12">
                            {/* TYPE HEADER */}
                            <div
                                className="font-size-16 p-2 fw-bold d-flex align-items-center justify-content-between mb-2"
                                style={{
                                    background: '#f8f9fa'
                                }}
                            >
                                <div>
                                    <i className="ri-building-4-line me-2" />

                                    {t(FinancialStatmentsTypes[type]?.title || type)}
                                    {FinancialStatmentsTypes[type]?.description ? (
                                        <>
                                            {' '}
                                            <span
                                                id={`tooltip-financials-${type}`}
                                                style={{ cursor: "pointer" }}>
                                                <RiInformationLine />
                                            </span>
                                            <UncontrolledTooltip
                                                target={`tooltip-financials-${type}`}
                                            >
                                                {type} <br/>
                                                {t(FinancialStatmentsTypes[type].description)}
                                            </UncontrolledTooltip>
                                        </>
                                    ) : null}
                                </div>
                            </div>

                            {renderSection(
                                "Balance Sheet",
                                "ri-bank-line",
                                sortedItems,
                                "balance_sheet"
                            )}

                            {renderSection(
                                "Profit & Loss",
                                "ri-line-chart-line",
                                sortedItems,
                                "profit_and_loss"
                            )}

                            {renderSection(
                                "Ratios",
                                "ri-percent-line",
                                sortedItems,
                                "ratios"
                            )}

                            {renderSection(
                                "Other Financials",
                                "ri-file-list-3-line",
                                sortedItems,
                                "other_financials"
                            )}

                        </Col>
                    </Row>
                )
            })}
        </div>
    )
}

export default withNamespaces()(
    LocalFinancialTable
)